import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiSave, HiX } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./CreateCategory.scss";

interface CategoryFormData {
  name: string;
  hsCode: string;
  description: string;
  tax: number;
}

interface CategoryFormErrors {
  name?: string;
  hsCode?: string;
  description?: string;
  tax?: string;
}

export const CreateCategory: React.FC = () => {
  const { t } = useTranslation(['categories', 'common']);
  const navigate = useNavigate();
  const { token } = useAuth();

  const [formData, setFormData] = useState<CategoryFormData>({
    name: "",
    hsCode: "",
    description: "",
    tax: 0,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<CategoryFormErrors>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'tax' ? parseFloat(value) || 0 : value,
    });

    // Clear error when user starts typing
    if (errors[name as keyof CategoryFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: CategoryFormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('categories:validation.nameRequired');
    } else if (formData.name.length < 2) {
      newErrors.name = t('categories:validation.nameMinLength');
    } else if (formData.name.length > 100) {
      newErrors.name = t('categories:validation.nameMaxLength');
    }

    if (!formData.hsCode.trim()) {
      newErrors.hsCode = t('categories:validation.hsCodeRequired');
    }

    if (formData.tax < 0) {
      newErrors.tax = t('categories:validation.taxMin');
    } else if (formData.tax > 100) {
      newErrors.tax = t('categories:validation.taxMax');
    }

    if (formData.description.length > 500) {
      newErrors.description = t('categories:validation.descriptionMaxLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await adminApi.call('/category', 'POST', formData);

      if (!response.success) {
        showErrorToast(response.error || t('categories:messages.createError'));
        return;
      }

      showSuccessToast(t('categories:messages.createSuccess'));

      // Navigate back to list after successful creation
      setTimeout(() => {
        navigate('/categories');
      }, 2000);

    } catch (error) {
      showErrorToast(t('categories:messages.createError'));
      console.error('Error creating category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/categories');
  };

  const breadcrumbs = [
    { label: t('categories:breadcrumbs.categories'), path: '/categories' },
    { label: t('categories:breadcrumbs.create') }
  ];

  const headerActions = (
    <div className="header-actions">
      <ActionButton
        variant="secondary"
        icon={HiX}
        onClick={handleCancel}
      >
        {t('common:cancel')}
      </ActionButton>
      <ActionButton
        variant="primary"
        icon={HiSave}
        onClick={handleSubmit}
        loading={loading}
        disabled={loading}
      >
        {t('common:save')}
      </ActionButton>
    </div>
  );

  return (
    <PageLayout
      title={t('categories:createCategory')}
      subtitle={t('categories:subtitle')}
      breadcrumbs={breadcrumbs}
      headerActions={headerActions}
    >
      <div className="create-category-form">
        <form onSubmit={handleSubmit} className="modern-form">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="name">
                {t('categories:fields.name')} <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t('categories:placeholders.name')}
                disabled={loading}
              />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="hsCode">
                {t('categories:fields.hsCode')} <span className="required">*</span>
              </label>
              <input
                type="text"
                id="hsCode"
                name="hsCode"
                className={`form-control ${errors.hsCode ? 'is-invalid' : ''}`}
                value={formData.hsCode}
                onChange={handleInputChange}
                placeholder={t('categories:placeholders.hsCode')}
                disabled={loading}
              />
              {errors.hsCode && <div className="invalid-feedback">{errors.hsCode}</div>}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="tax">
                {t('categories:fields.tax')} <span className="required">*</span>
              </label>
              <input
                type="number"
                id="tax"
                name="tax"
                min="0"
                max="100"
                step="0.01"
                className={`form-control ${errors.tax ? 'is-invalid' : ''}`}
                value={formData.tax}
                onChange={handleInputChange}
                placeholder={t('categories:placeholders.tax')}
                disabled={loading}
                required
              />
              {errors.tax && <div className="invalid-feedback">{errors.tax}</div>}
            </div>

            <div className="form-group full-width">
              <label className="form-label" htmlFor="description">
                {t('categories:fields.description')} <span className="required">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                value={formData.description}
                onChange={handleInputChange}
                placeholder={t('categories:placeholders.description')}
                disabled={loading}
                required
              />
              {errors.description && <div className="invalid-feedback">{errors.description}</div>}
            </div>
          </div>
        </form>
      </div>
    </PageLayout>
  );
};
